import { colors, fonts } from 'app-v2/design-systems'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Text } from 'app/common/typography'
import { Cancel, CloseBoldIcon } from 'app/assets/icons'
import { Loader } from 'app/common/loader'
import type { TPKState } from 'shared/store/tu-preferes-king'
import type { RootState } from 'shared/store/reducers'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Exclamation, StyledCheck } from 'app-v2/assets/icons'
import { StyledStockText } from '../styles'
import { BORDER, COLOR, SPACING } from 'app-v2/design-tokens'
import { SplitSearch } from 'app/assets/icons'
import { breakpoints } from 'app/assets/styles'
import { TPKColors } from '../../colors'

const availabilityThreshold = {
    soldOut: 0,
    low: 15,
    medium: 40
}

export function StoreAvailability({
    loading,
    restaurantName
}: {
    loading: boolean,
    restaurantName: string
}) {
    const state: TPKState = useSelector((s: RootState) => s.tuPreferesKingState)
    const intl = useIntl()

    const [width, setWidth] = useState(window.innerWidth)
    const isSmallScreen = width < breakpoints.xsmallLegacy

    useEffect(() => {
        return window.addEventListener('resize', updateWindowDimensions)
    }, [])

    const updateWindowDimensions = () => setWidth(window.innerWidth)

    return (
        <MessageContainer>
            {state.error && (
                <>
                    <Cancel size={80} color={colors.fieryRed} />
                    <ErrorTextContainer>
                        <StyledStockText fontFamily={fonts.flameSansRegular}>
                            {intl.formatMessage({
                                id: 'tupreferesking.goodies.availabilityError'
                            })}
                        </StyledStockText>
                    </ErrorTextContainer>
                </>
            )}
            {!state.error && loading && <Loader loading={loading} />}
            {!state.error &&
                !loading &&
                !!state.availability &&
                state.availability < availabilityThreshold.soldOut && (
                    <>
                        <SplitSearch size={120} color={COLOR.PRIMARY} />
                        <Text font={fonts.flameRegular}>
                            {intl.formatMessage({
                                id:
                                    'tupreferesking.goodies.selectGainAndRestaurantCaption'
                            })}
                        </Text>
                    </>
                )}
            {!state.error &&
                !loading &&
                (!!state.availability || state.availability === 0) &&
                state.availability >= availabilityThreshold.soldOut &&
                state.availability < availabilityThreshold.low && (
                    <>
                        <CloseBoldIcon size={80} color={colors.fieryRed} />
                        <Text font={fonts.flameRegular}>
                            {intl.formatMessage({
                                id: 'tupreferesking.goodies.gainAlmostSoldOut'
                            })}
                            {restaurantName}
                        </Text>
                        <StockInfosContainer
                            availability={state.availability}
                            isSmallScreen={isSmallScreen}>
                            <StyledStockText
                                fontFamily={fonts.flameSansRegular}>
                                {intl.formatMessage({
                                    id:
                                        'tupreferesking.goodies.gainSoldOutCaption'
                                })}
                            </StyledStockText>
                        </StockInfosContainer>
                    </>
                )}
            {!state.error &&
                !loading &&
                !!state.availability &&
                state.availability >= availabilityThreshold.low &&
                state.availability <= availabilityThreshold.medium && (
                    <>
                        <Exclamation
                            size={80}
                            exclamationColor={colors.flamingOrange}
                        />
                        <Text font={fonts.flameRegular}>
                            {intl.formatMessage({
                                id: 'tupreferesking.goodies.gainStockRatherLow'
                            })}
                            {restaurantName}
                        </Text>
                        <StockInfosContainer
                            availability={state.availability}
                            isSmallScreen={isSmallScreen}>
                            <StyledStockText
                                fontFamily={fonts.flameSansRegular}>
                                {intl.formatMessage({
                                    id: 'tupreferesking.goodies.getGainCaption'
                                })}
                            </StyledStockText>
                        </StockInfosContainer>
                    </>
                )}
            {!state.error &&
                !loading &&
                !!state.availability &&
                state.availability &&
                state.availability > availabilityThreshold.medium && (
                    <>
                        <StyledCheck />
                        <Text font={fonts.flameRegular}>
                            {intl.formatMessage({
                                id: 'tupreferesking.goodies.gainAvailable'
                            })}{' '}
                            {restaurantName}{' '}
                        </Text>
                        <StockInfosContainer
                            availability={state.availability}
                            isSmallScreen={isSmallScreen}>
                            <StyledStockText
                                fontFamily={fonts.flameSansRegular}>
                                {intl.formatMessage({
                                    id: 'tupreferesking.goodies.getGainCaption'
                                })}
                            </StyledStockText>
                        </StockInfosContainer>
                    </>
                )}
        </MessageContainer>
    )
}

const MessageContainer = styled.div`
    background-color: ${TPKColors.veryLightMayo};
    width: 100%;
    min-width: 50%;
    min-height: 100%;
    border-radius: ${BORDER.RADIUS.M};
    margin-top: ${SPACING.L};
    padding: ${SPACING.XXXL} ${SPACING.L};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${SPACING.XL};
    text-align: center;
`

const StockInfosContainer = styled.div`
    background-color: ${props => {
        const availability = props.availability
        if (
            availability >= availabilityThreshold.soldOut &&
            availability < availabilityThreshold.low
        ) {
            return colors.lightRed
        } else if (
            availability >= availabilityThreshold.low &&
            availability <= availabilityThreshold.medium
        ) {
            return colors.lightOrange
        } else {
            return `${colors.lightGreen}52`
        }
    }};
    padding: ${SPACING.M};
    width: 90%;
    border-radius: ${BORDER.RADIUS.M};
    text-align: center;
`
const ErrorTextContainer = styled.div`
    display: flex;
    max-width: 60%;
    align-items: center;
`
